import { Grid, Icon, List, Popup } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import {
  formatDate,
  formatDuration,
  formatLanguagesForCampaign,
  getCountryCode,
  getIsRequiredResolutionDelivered,
} from "services/common";
import { ResolutionType, CampaignType } from "models";
import { useSelector } from "react-redux";
import { getAgencys } from "store/selectors";

const CAMPAIGN_KEYS = [
  "advertiser",
  "type",
  "contract_number",
  "start_time",
  "end_time",
  "deliveryDeadline",
  "createdAt",
  "updatedAt",
  "adminNotes",
  "notes",
  "scheduling_notes",
  "status",
  "printSizes",
  "requiredResolutions",
  "duration",
  "languages",
  "orderlineID",
  "sales_segment",
  "salesperson",
  "salesperson_contact",
  "mediaAgencyID",
  "mediaDeliveryPersonContacts",
];

// Groups given active native resolutions under their required resolutions
export const getGroupedActiveNativeResolutions = (requiredResolutions, nativeResolutions) => {
  const groupedNativeResolutions = _.reduce(
    requiredResolutions,
    function (result, requiredResolution) {
      const requiredResolutionID = requiredResolution.requiredResolutionID;
      const nativeResolutionsForRequiredRes = _.filter(nativeResolutions, function (nativeRes) {
        return (
          nativeRes.nativeResolution.requiredResolutionID === requiredResolutionID &&
          nativeRes.nativeResolution.active
        );
      });
      return nativeResolutionsForRequiredRes.length === 0
        ? result
        : [
            ...result,
            {
              requiredResolution,
              nativeResolutions: nativeResolutionsForRequiredRes,
            },
          ];
    },
    []
  );
  return groupedNativeResolutions;
};

export const formatPrintSizes = (printSizes, t) => {
  if (printSizes && printSizes.length === 0) return t("all_print_sizes_are_allowed_msg");
  return (
    <List as="ol">
      {_.map(printSizes, ({ printSize }) => {
        if (!printSize) {
          return (
            <List.Item as="li" value="-" key={new Date().getTime()}>
              <List.Content>-</List.Content>
            </List.Item>
          );
        }
        return (
          <List.Item as="li" value="-" key={printSize.id}>
            <List.Content>{printSize.name}</List.Content>
          </List.Item>
        );
      })}
    </List>
  );
};

export const formatResolutions = (
  requiredResolutions,
  nativeResolutions,
  isAdmin,
  t,
  deliveredAssetResolutions = [],
  deliveredImageAssetResolutions = []
) => {
  const ALL_DIGITAL_SIZES_ARE_ALLOWED_MSG = t("all_digital_sizes_are_allowed_msg");
  if (requiredResolutions.items.length === 0) return ALL_DIGITAL_SIZES_ARE_ALLOWED_MSG;

  const groupedActiveNativeResolutions = getGroupedActiveNativeResolutions(
    requiredResolutions.items,
    nativeResolutions.items
  );

  if (groupedActiveNativeResolutions.length === 0) return ALL_DIGITAL_SIZES_ARE_ALLOWED_MSG;

  return (
    <div className="resolution-display" style={!isAdmin ? { flexDirection: "column" } : {}}>
      {_.map(groupedActiveNativeResolutions, function (rrNrPair) {
        const requiredResolution = rrNrPair.requiredResolution.requiredResolution;
        const nativeResolutions = _.map(rrNrPair.nativeResolutions, "nativeResolution");
        const rrType = requiredResolution.type;
        // Search only from JPG and PNG assets if the Required Resolution is of type IMAGE,
        // otherwise from both type assets
        const assetResolutionsToSearch =
          rrType === ResolutionType.IMAGE
            ? deliveredImageAssetResolutions
            : deliveredAssetResolutions;
        const isRequiredResolutionDelivered = getIsRequiredResolutionDelivered(
          requiredResolution,
          nativeResolutions,
          assetResolutionsToSearch
        );
        const color = isRequiredResolutionDelivered ? "green" : "yellow";
        const iconName = isRequiredResolutionDelivered ? "check" : "warning";

        return isAdmin ? (
          // Admin view
          <div
            key={requiredResolution.id}
            className="admin-required-resolution-display"
            style={{ border: `2px solid ${color === "yellow" ? "#ffbd59" : "green"}` }}
          >
            <List>
              <Popup
                content={
                  isRequiredResolutionDelivered
                    ? t("campaign_info_resolution_delivered")
                    : t("campaign_info_resolution_not_delivered")
                }
                trigger={
                  <List.Item>
                    <b>{`${requiredResolution.value} (${
                      rrType === ResolutionType.IMAGE ? "jpg/png" : "mp4/mov/jpg/png"
                    })`}</b>
                    <Icon name={iconName} color={color} />
                  </List.Item>
                }
                position="top center"
              />
              <List as="ol">
                {_.map(nativeResolutions, function (nativeResolution) {
                  const isNativeResolutionDelivered = _.includes(
                    assetResolutionsToSearch,
                    nativeResolution.value
                  );
                  const color = isNativeResolutionDelivered ? "green" : "yellow";
                  const iconName = isNativeResolutionDelivered ? "check" : "warning";
                  return (
                    <List.Item
                      as="li"
                      value="-"
                      key={nativeResolution.id}
                      style={{ paddingLeft: "13px" }}
                    >
                      <List.Content>
                        {nativeResolution.value}
                        {!isRequiredResolutionDelivered ? (
                          <Icon name={iconName} color={color} style={{ marginLeft: ".5rem" }} />
                        ) : null}
                      </List.Content>
                    </List.Item>
                  );
                })}
              </List>
            </List>
          </div>
        ) : (
          // Contributor view
          <div key={requiredResolution.id} className="contributor-required-resolution-display">
            <Popup
              content={
                isRequiredResolutionDelivered
                  ? t("campaign_info_resolution_delivered")
                  : t("campaign_info_resolution_not_delivered")
              }
              trigger={
                <li>
                  <span>
                    {requiredResolution.value} (
                    {requiredResolution.type === ResolutionType.IMAGE
                      ? t("image_required")
                      : t("image_or_video_required")}
                    )
                    <Icon name={iconName} color={color} />
                  </span>
                </li>
              }
              position="top center"
            />
          </div>
        );
      })}
    </div>
  );
};

export const formatMediaDeliveryPersonContacts = (contacts) => {
  return (
    <List as="ol">
      {_.map(contacts, (contact, i) => {
        return (
          <List.Item as="li" value="-" key={_.uniqueId(i)}>
            <List.Content>{contact}</List.Content>
          </List.Item>
        );
      })}
    </List>
  );
};

const CampaignInfo = ({
  campaign,
  onEdit,
  deliveredAssetResolutions,
  deliveredImageAssetResolutions,
  isCampaignActive,
  isAdmin,
  activeUser,
  isOwner,
}) => {
  let userAllowedInfo = !isAdmin
    ? CAMPAIGN_KEYS.filter(
        (key) =>
          ![
            "createdAt",
            "updatedAt",
            "status",
            "orderlineID",
            "salesperson",
            "salesperson_contact",
            "mediaDeliveryPersonContacts",
            "adminNotes",
            "mediaAgencyID",
          ].includes(key)
      )
    : CAMPAIGN_KEYS;

  const agencies = useSelector(getAgencys);

  const getAgencyName = (agencyID) => {
    const agency = agencies.items.find((agency) => agency.id === agencyID);
    return agency ? agency.name : "";
  };

  if (!["be", "dev"].includes(getCountryCode())) {
    userAllowedInfo = userAllowedInfo.filter((key) => "languages" !== key);
  }
  const { t } = useTranslation("common");
  const isCollaborator = () => {
    const { collaborators, mediaCollaborators } = campaign;

    const allCollaborators = [
      ...(Array.isArray(collaborators) ? collaborators : []),
      ...(Array.isArray(mediaCollaborators) ? mediaCollaborators : []),
    ];

    return allCollaborators.includes(activeUser.username);
  };

  // Contributors can edit campaign if it's active AND user is either the owner or in the collaborators list

  if (!campaign) return null;
  return (
    <div>
      <h2 className="italic">
        {campaign.name}
        {onEdit &&
          (isAdmin || (isCampaignActive && isOwner) || (isCampaignActive && isCollaborator())) && (
            <button type="button" className="naked-button" onClick={onEdit}>
              <Icon name="edit outline" /> {t("campaign_info_edit_button")}
            </button>
          )}
      </h2>
      <Grid columns={2} className="info-grid">
        {userAllowedInfo.map((key) =>
          // Print sizes
          key === "printSizes" && campaign[key] ? (
            campaign["type"] && campaign["type"] === CampaignType.PRINT ? (
              <Grid.Row key={key}>
                <Grid.Column width={3}>{t("campaign_" + key)}</Grid.Column>
                <Grid.Column>{formatPrintSizes(campaign.printSizes.items, t)}</Grid.Column>
              </Grid.Row>
            ) : null
          ) : // Digital sizes
          key === "requiredResolutions" && campaign[key] ? (
            campaign["type"] && campaign["type"] === CampaignType.DIGITAL ? (
              <Grid.Row key={key}>
                <Grid.Column width={3}>{t("campaign_" + key)}</Grid.Column>
                <Grid.Column>
                  {formatResolutions(
                    campaign[key],
                    campaign["nativeResolutions"],
                    isAdmin,
                    t,
                    deliveredAssetResolutions,
                    deliveredImageAssetResolutions
                  )}
                </Grid.Column>
              </Grid.Row>
            ) : null
          ) : key === "duration" && campaign[key] ? (
            campaign["type"] && campaign["type"] === CampaignType.DIGITAL ? (
              <Grid.Row key={key}>
                <Grid.Column width={3}>{t("campaign_video_durations")}</Grid.Column>
                <Grid.Column>{formatDuration(campaign[key])}</Grid.Column>
              </Grid.Row>
            ) : null
          ) : key === "languages" && campaign[key] ? (
            <Grid.Row key={key}>
              <Grid.Column width={3}>{t("campaign_languages")}</Grid.Column>
              <Grid.Column>{formatLanguagesForCampaign(campaign[key])}</Grid.Column>
            </Grid.Row>
          ) : // List of media delivery person contacts
          key === "mediaAgencyID" && campaign[key] ? (
            campaign[key].length > 0 && (
              <Grid.Row key={key}>
                <Grid.Column width={3}>{t("campaign_media_agency")}</Grid.Column>
                <Grid.Column>{getAgencyName(campaign[key])}</Grid.Column>
              </Grid.Row>
            )
          ) : key === "mediaDeliveryPersonContacts" && campaign[key] ? (
            campaign[key].length > 0 && (
              <Grid.Row key={key}>
                <Grid.Column width={3}>{t("campaign_" + key)}</Grid.Column>
                <Grid.Column>{formatMediaDeliveryPersonContacts(campaign[key])}</Grid.Column>
              </Grid.Row>
            )
          ) : // Rest of the keys
          campaign[key] ? (
            <Grid.Row key={key}>
              <Grid.Column width={3}>{t("campaign_" + key)}</Grid.Column>
              <Grid.Column style={{ whiteSpace: "pre-wrap" }}>
                {key === "createdAt" ||
                key === "updatedAt" ||
                key === "end_time" ||
                key === "start_time" ||
                key === "deliveryDeadline"
                  ? formatDate(campaign[key])
                  : key === "status"
                  ? _.replace(campaign[key].slice(), /_/g, " ") // e.g. "AWAITING_DELIVERY" -> "AWAITING DELIVERY"
                  : campaign[key]}
              </Grid.Column>
            </Grid.Row>
          ) : null
        )}
      </Grid>
    </div>
  );
};

export default CampaignInfo;
